import {
  Cross2Icon,
  ExternalLinkIcon,
  LockClosedIcon,
  Pencil1Icon,
} from "@radix-ui/react-icons";
import {
  Box,
  Button,
  Callout,
  DataList,
  Dialog,
  Flex,
  Heading,
  IconButton,
  Link,
  Separator,
  Skeleton,
  Text,
  TextArea,
  TextField,
  VisuallyHidden,
} from "@radix-ui/themes";
import { FormEvent, ReactNode, useCallback, useEffect, useState } from "react";
import {
  BOTTLE_FACT_HELP_TEXT,
  NOT_LISTED_BY_SOURCE,
  parseAbv,
  parseVolumeMl,
} from "./bottleFacts";
import {
  ContentType,
  DraftUpdatePayload,
  EditorialRecordDetail,
  EditorialStatus,
  PublicationStatus,
} from "./client";
import { Field, NativeSelect, StatusBadge } from "./ui";

export type DetailTarget = {
  type: ContentType;
  id: string;
  name: string;
};

type BrandOption = { id: string; name: string };

type DetailApi = {
  getRecordDetail: (
    type: ContentType,
    id: string,
  ) => Promise<EditorialRecordDetail>;
  updateDraft: (payload: DraftUpdatePayload) => Promise<void>;
};

type FormValues = Record<string, string>;
type FormErrors = Record<string, string>;

const typeLabels: Record<ContentType, string> = {
  brand: "Brand",
  bottle: "Bottle",
  guide: "Guide",
};

export const editorialStatusLabels: Record<EditorialStatus, string> = {
  "editorially-sourced": "Editorially sourced",
  "user-reported-unverified": "User reported, unverified",
  "official-record-link-available": "Official record link available",
  "official-response-received": "Official response received",
};

// The UI only edits drafts and records sent back for correction. The API also
// accepts a PATCH on in_review content; that gap is closed server-side, not
// exposed here.
export const EDITABLE_STATUSES: PublicationStatus[] = [
  "draft",
  "correction_required",
];

const lockedReasons: Partial<Record<PublicationStatus, string>> = {
  in_review:
    "In review, so it is locked. A reviewer can request a correction to reopen it for editing.",
  published:
    "Published, so it is locked. An administrator must roll it back to draft before it can be edited.",
};

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function recordName(detail: EditorialRecordDetail) {
  const { record } = detail;
  return record.type === "guide" ? record.data.title : record.data.name;
}

function toFormValues(detail: EditorialRecordDetail): FormValues {
  const { record } = detail;
  const common = {
    slug: record.data.slug,
    editorial_status: record.data.editorial_status,
    last_reviewed: record.data.last_reviewed,
  };

  if (record.type === "brand") {
    return {
      ...common,
      name: record.data.name,
      aliases: record.data.aliases.join("\n"),
      category: record.data.category,
      origin: record.data.origin,
      history: record.data.history,
    };
  }

  if (record.type === "bottle") {
    return {
      ...common,
      brand_id: record.data.brand_id,
      name: record.data.name,
      category: record.data.category,
      origin: record.data.origin,
      description: record.data.description,
      // Unknown stays blank in the form and is sent back as null.
      abv: record.data.abv === null ? "" : String(record.data.abv),
      volume_ml:
        record.data.volume_ml === null ? "" : String(record.data.volume_ml),
    };
  }

  return {
    ...common,
    title: record.data.title,
    summary: record.data.summary,
    body: record.data.body,
  };
}

// Mirrors the draft schema in user-site src/lib/editorial-store.ts so most
// mistakes are caught before the request. The API still has the final say.
function requireText(
  values: FormValues,
  errors: FormErrors,
  key: string,
  label: string,
  max: number,
) {
  const value = (values[key] ?? "").trim();

  if (!value) {
    errors[key] = `${label} is required.`;
  } else if (value.length > max) {
    errors[key] = `${label} must be at most ${max} characters.`;
  }

  return value;
}

export function buildDraftUpdate(
  detail: EditorialRecordDetail,
  values: FormValues,
): { errors: FormErrors; payload: DraftUpdatePayload | null } {
  const errors: FormErrors = {};
  const { record } = detail;
  const slug = (values.slug ?? "").trim();

  if (!SLUG_PATTERN.test(slug) || slug.length > 160) {
    errors.slug =
      "Use lowercase letters, numbers and single hyphens, e.g. tusker-lager.";
  }

  const lastReviewed = (values.last_reviewed ?? "").trim();

  if (!DATE_PATTERN.test(lastReviewed)) {
    errors.last_reviewed = "Enter the review date.";
  }

  const editorialStatus = values.editorial_status as EditorialStatus;
  let payload: DraftUpdatePayload;

  if (record.type === "brand") {
    const aliases = (values.aliases ?? "")
      .split("\n")
      .map((alias) => alias.trim())
      .filter(Boolean);

    if (aliases.length > 100) {
      errors.aliases = "A brand can have at most 100 aliases.";
    } else if (aliases.some((alias) => alias.length > 250)) {
      errors.aliases = "Each alias must be at most 250 characters.";
    }

    payload = {
      id: record.id,
      type: "brand",
      data: {
        ...record.data,
        name: requireText(values, errors, "name", "Name", 250),
        slug,
        aliases,
        category: requireText(values, errors, "category", "Category", 250),
        origin: requireText(values, errors, "origin", "Origin", 250),
        history: requireText(values, errors, "history", "History", 10000),
        editorial_status: editorialStatus,
        last_reviewed: lastReviewed,
      },
    };
  } else if (record.type === "bottle") {
    const abv = parseAbv(values.abv ?? "");
    const volumeMl = parseVolumeMl(values.volume_ml ?? "");

    if (!abv.ok) {
      errors.abv = abv.error;
    }

    if (!volumeMl.ok) {
      errors.volume_ml = volumeMl.error;
    }

    if (!values.brand_id) {
      errors.brand_id = "Select the related brand.";
    }

    payload = {
      id: record.id,
      type: "bottle",
      data: {
        ...record.data,
        brand_id: values.brand_id ?? "",
        name: requireText(values, errors, "name", "Name", 250),
        slug,
        category: requireText(values, errors, "category", "Category", 250),
        origin: requireText(values, errors, "origin", "Origin", 250),
        description: requireText(
          values,
          errors,
          "description",
          "Description",
          10000,
        ),
        // Blank means the source does not state it: null, never 0.
        abv: abv.ok ? abv.value : null,
        volume_ml: volumeMl.ok ? volumeMl.value : null,
        editorial_status: editorialStatus,
        last_reviewed: lastReviewed,
      },
    };
  } else {
    payload = {
      id: record.id,
      type: "guide",
      data: {
        ...record.data,
        title: requireText(values, errors, "title", "Title", 250),
        slug,
        summary: requireText(values, errors, "summary", "Summary", 10000),
        body: requireText(values, errors, "body", "Body", 10000),
        editorial_status: editorialStatus,
        last_reviewed: lastReviewed,
      },
    };
  }

  return Object.keys(errors).length > 0
    ? { errors, payload: null }
    : { errors, payload };
}

function formatAbv(abv: number | null) {
  return abv === null ? NOT_LISTED_BY_SOURCE : `${abv}%`;
}

function formatVolume(volumeMl: number | null) {
  return volumeMl === null ? NOT_LISTED_BY_SOURCE : `${volumeMl} ml`;
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <DataList.Item>
      <DataList.Label minWidth="120px">{label}</DataList.Label>
      <DataList.Value>{children}</DataList.Value>
    </DataList.Item>
  );
}

function LongText({ label, text }: { label: string; text: string }) {
  return (
    <Flex direction="column" gap="1">
      <Heading as="h3" size="2">
        {label}
      </Heading>
      <Text as="p" className="gw-long-text" size="2">
        {text}
      </Text>
    </Flex>
  );
}

function RecordFacts({ detail }: { detail: EditorialRecordDetail }) {
  const { record, meta } = detail;

  return (
    <Flex direction="column" gap="4">
      <DataList.Root orientation={{ initial: "vertical", xs: "horizontal" }} size="2">
        <DetailRow label="Slug">
          <Text className="gw-break">{record.data.slug}</Text>
        </DetailRow>
        <DetailRow label="Status">
          <StatusBadge status={meta.publication_status} />
        </DetailRow>
        {record.type === "bottle" && (
          <DetailRow label="Brand">{meta.brand_name ?? "Unknown brand"}</DetailRow>
        )}
        {record.type !== "guide" && (
          <>
            <DetailRow label="Category">{record.data.category}</DetailRow>
            <DetailRow label="Origin">{record.data.origin}</DetailRow>
          </>
        )}
        {record.type === "brand" && (
          <DetailRow label="Aliases">
            {record.data.aliases.length > 0
              ? record.data.aliases.join(", ")
              : "None"}
          </DetailRow>
        )}
        {record.type === "bottle" && (
          <>
            <DetailRow label="ABV">{formatAbv(record.data.abv)}</DetailRow>
            <DetailRow label="Volume">
              {formatVolume(record.data.volume_ml)}
            </DetailRow>
          </>
        )}
        <DetailRow label="Editorial status">
          {editorialStatusLabels[record.data.editorial_status] ??
            record.data.editorial_status}
        </DetailRow>
        <DetailRow label="Review date">{record.data.last_reviewed}</DetailRow>
        {meta.updated_at && (
          <DetailRow label="Last updated">
            {meta.updated_at.slice(0, 10)}
          </DetailRow>
        )}
      </DataList.Root>

      {record.type === "brand" && (
        <LongText label="History" text={record.data.history} />
      )}
      {record.type === "bottle" && (
        <LongText label="Description" text={record.data.description} />
      )}
      {record.type === "guide" && (
        <>
          <LongText label="Summary" text={record.data.summary} />
          <LongText label="Body" text={record.data.body} />
        </>
      )}
    </Flex>
  );
}

function Citations({ detail }: { detail: EditorialRecordDetail }) {
  return (
    <section aria-labelledby="detail-citations-heading">
      <Heading as="h3" id="detail-citations-heading" mb="2" size="3">
        Citations
      </Heading>
      {detail.citations.length === 0 ? (
        <Text as="p" color="gray" size="2">
          No citations yet. Attach a source before submitting for review.
        </Text>
      ) : (
        <ul className="gw-queue">
          {detail.citations.map((citation) => (
            <li key={citation.source_id}>
              <Flex direction="column" gap="1" py="2">
                <Link
                  className="gw-break"
                  href={citation.url}
                  rel="noopener noreferrer"
                  size="2"
                  target="_blank"
                  weight="medium"
                >
                  {citation.title}{" "}
                  <ExternalLinkIcon aria-hidden className="gw-inline-icon" />
                </Link>
                <Text color="gray" size="1">
                  {citation.publisher} · Accessed {citation.accessed_at}
                </Text>
                <Text className="gw-break" color="gray" size="1">
                  {citation.url}
                </Text>
              </Flex>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DetailSkeleton() {
  return (
    <Flex aria-hidden direction="column" gap="3">
      {[0, 1, 2, 3, 4].map((row) => (
        <Flex gap="4" key={row}>
          <Skeleton height="18px" width="110px" />
          <Skeleton height="18px" width="50%" />
        </Flex>
      ))}
      <Skeleton height="72px" mt="2" />
    </Flex>
  );
}

function ErrorText({ id, error }: { id: string; error?: string }) {
  if (!error) {
    return null;
  }

  return (
    <Text color="red" id={id} role="alert" size="1" weight="medium">
      {error}
    </Text>
  );
}

function EditForm({
  brands,
  detail,
  errors,
  isSubmitting,
  onCancel,
  onChange,
  onSubmit,
  saveError,
  values,
}: {
  brands: BrandOption[];
  detail: EditorialRecordDetail;
  errors: FormErrors;
  isSubmitting: boolean;
  onCancel: () => void;
  onChange: (key: string, value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  saveError: string;
  values: FormValues;
}) {
  const { record, meta } = detail;
  const brandOptions =
    record.type === "bottle" &&
    !brands.some((brand) => brand.id === record.data.brand_id)
      ? [
          { id: record.data.brand_id, name: meta.brand_name ?? "Current brand" },
          ...brands,
        ]
      : brands;

  function textField(
    key: string,
    label: string,
    extra: { help?: string; inputMode?: "decimal" | "numeric"; type?: "date" } = {},
  ) {
    const id = `detail-${key}`;
    const error = errors[key];

    return (
      <Field htmlFor={id} label={label}>
        <TextField.Root
          aria-describedby={
            [extra.help ? `${id}-help` : "", error ? `${id}-error` : ""]
              .filter(Boolean)
              .join(" ") || undefined
          }
          aria-invalid={error ? true : undefined}
          className={error ? "gw-invalid" : undefined}
          color={error ? "red" : undefined}
          id={id}
          inputMode={extra.inputMode}
          onChange={(event) => onChange(key, event.target.value)}
          type={extra.type}
          value={values[key] ?? ""}
        />
        {extra.help && (
          <Text color="gray" id={`${id}-help`} size="1">
            {extra.help}
          </Text>
        )}
        <ErrorText error={error} id={`${id}-error`} />
      </Field>
    );
  }

  function textArea(key: string, label: string, rows: number, help?: string) {
    const id = `detail-${key}`;
    const error = errors[key];

    return (
      <Field htmlFor={id} label={label}>
        <TextArea
          aria-describedby={
            [help ? `${id}-help` : "", error ? `${id}-error` : ""]
              .filter(Boolean)
              .join(" ") || undefined
          }
          aria-invalid={error ? true : undefined}
          className={error ? "gw-invalid" : undefined}
          color={error ? "red" : undefined}
          id={id}
          onChange={(event) => onChange(key, event.target.value)}
          resize="vertical"
          rows={rows}
          value={values[key] ?? ""}
        />
        {help && (
          <Text color="gray" id={`${id}-help`} size="1">
            {help}
          </Text>
        )}
        <ErrorText error={error} id={`${id}-error`} />
      </Field>
    );
  }

  const originError = errors.origin;

  return (
    <form noValidate onSubmit={onSubmit}>
      <Flex direction="column" gap="4">
        {record.type === "guide"
          ? textField("title", "Title")
          : textField("name", "Name")}
        {textField("slug", "Slug")}
        {record.type === "bottle" && (
          <Field htmlFor="detail-brand_id" label="Brand">
            <NativeSelect
              aria-describedby={
                errors.brand_id ? "detail-brand_id-error" : undefined
              }
              id="detail-brand_id"
              onChange={(event) => onChange("brand_id", event.target.value)}
              value={values.brand_id ?? ""}
            >
              <option value="">Select a brand</option>
              {brandOptions.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </NativeSelect>
            <ErrorText error={errors.brand_id} id="detail-brand_id-error" />
          </Field>
        )}
        {record.type !== "guide" && (
          <>
            {textField("category", "Category")}
            <Field htmlFor="detail-origin" label="Origin">
              <Flex gap="2" wrap="wrap">
                <Box flexBasis="180px" flexGrow="1">
                  <TextField.Root
                    aria-describedby={
                      originError ? "detail-origin-error" : undefined
                    }
                    aria-invalid={originError ? true : undefined}
                    className={originError ? "gw-invalid" : undefined}
                    color={originError ? "red" : undefined}
                    id="detail-origin"
                    onChange={(event) => onChange("origin", event.target.value)}
                    value={values.origin ?? ""}
                  />
                </Box>
                <Button
                  color="gray"
                  onClick={() => onChange("origin", NOT_LISTED_BY_SOURCE)}
                  type="button"
                  variant="soft"
                >
                  {NOT_LISTED_BY_SOURCE}
                </Button>
              </Flex>
              <ErrorText error={originError} id="detail-origin-error" />
            </Field>
          </>
        )}
        {record.type === "brand" &&
          textArea("aliases", "Aliases", 3, "One alias per line.")}
        {record.type === "bottle" && (
          <Flex direction={{ initial: "column", xs: "row" }} gap="4">
            <Box flexGrow="1" flexBasis="0">
              {textField("abv", "ABV (%)", {
                help: BOTTLE_FACT_HELP_TEXT,
                inputMode: "decimal",
              })}
            </Box>
            <Box flexGrow="1" flexBasis="0">
              {textField("volume_ml", "Volume (ml)", {
                help: BOTTLE_FACT_HELP_TEXT,
                inputMode: "numeric",
              })}
            </Box>
          </Flex>
        )}
        {record.type === "brand" && textArea("history", "History", 6)}
        {record.type === "bottle" &&
          textArea("description", "Description", 6)}
        {record.type === "guide" && (
          <>
            {textArea("summary", "Summary", 3)}
            {textArea("body", "Body", 10)}
          </>
        )}
        <Field htmlFor="detail-editorial_status" label="Editorial status">
          <NativeSelect
            id="detail-editorial_status"
            onChange={(event) =>
              onChange("editorial_status", event.target.value)
            }
            value={values.editorial_status ?? ""}
          >
            {(Object.keys(editorialStatusLabels) as EditorialStatus[]).map(
              (status) => (
                <option key={status} value={status}>
                  {editorialStatusLabels[status]}
                </option>
              ),
            )}
          </NativeSelect>
        </Field>
        {textField("last_reviewed", "Review date", { type: "date" })}

        {saveError && (
          <Callout.Root color="red" role="alert" size="1">
            <Callout.Text>{saveError}</Callout.Text>
          </Callout.Root>
        )}

        <Flex gap="3" justify="end" wrap="wrap">
          <Button
            color="gray"
            disabled={isSubmitting}
            onClick={onCancel}
            type="button"
            variant="soft"
          >
            Cancel
          </Button>
          <Button disabled={isSubmitting} type="submit">
            {isSubmitting ? "Saving..." : "Save draft"}
          </Button>
        </Flex>
      </Flex>
    </form>
  );
}

export function RecordDetailDialog({
  actions,
  api,
  brands,
  onOpenChange,
  onSaved,
  open,
  refreshKey,
  target,
}: {
  actions?: ReactNode;
  api: DetailApi;
  brands: BrandOption[];
  onOpenChange: (open: boolean) => void;
  onSaved: () => Promise<void>;
  open: boolean;
  refreshKey: number;
  target: DetailTarget | null;
}) {
  const [detail, setDetail] = useState<EditorialRecordDetail | null>(null);
  const [loadError, setLoadError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [values, setValues] = useState<FormValues>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [saveError, setSaveError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  const targetType = target?.type;
  const targetId = target?.id;

  const load = useCallback(
    async (type: ContentType, id: string) => api.getRecordDetail(type, id),
    [api],
  );

  useEffect(() => {
    if (!open || !targetType || !targetId) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLoadError("");
    setDetail((current) =>
      current?.record.id === targetId && current.record.type === targetType
        ? current
        : null,
    );

    load(targetType, targetId)
      .then((next) => {
        if (!cancelled) {
          setDetail(next);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : String(error));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [load, open, refreshKey, retryKey, targetId, targetType]);

  useEffect(() => {
    if (!open) {
      setIsEditing(false);
      setErrors({});
      setSaveError("");
    }
  }, [open]);

  const status = detail?.meta.publication_status;
  const canEdit = status ? EDITABLE_STATUSES.includes(status) : false;
  const lockedReason = status ? lockedReasons[status] : undefined;
  const title = detail ? recordName(detail) : target?.name ?? "Record";

  function startEditing() {
    if (!detail) {
      return;
    }

    setValues(toFormValues(detail));
    setErrors({});
    setSaveError("");
    setIsEditing(true);
  }

  function changeValue(key: string, value: string) {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      if (!current[key]) {
        return current;
      }

      const next = { ...current };
      delete next[key];
      return next;
    });
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!detail) {
      return;
    }

    const result = buildDraftUpdate(detail, values);
    setErrors(result.errors);

    if (!result.payload) {
      setSaveError("Fix the highlighted fields before saving.");
      return;
    }

    setIsSubmitting(true);
    setSaveError("");

    try {
      await api.updateDraft(result.payload);
      const next = await api.getRecordDetail(
        result.payload.type,
        result.payload.id,
      );
      setDetail(next);
      setIsEditing(false);
      await onSaved();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : String(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Content
        aria-describedby={undefined}
        maxWidth="720px"
        // The workspace notice sits above the overlay; dismissing it must
        // not close the record.
        onPointerDownOutside={(event) => {
          const element = event.target as Element | null;

          if (element?.closest?.(".gw-toast-region")) {
            event.preventDefault();
          }
        }}
        size={{ initial: "2", sm: "4" }}
      >
        <Flex align="start" gap="3" justify="between" mb="1">
          <Flex direction="column" gap="1" minWidth="0">
            <Text color="gray" size="2">
              {target ? typeLabels[target.type] : ""}
              {isEditing ? " · Editing" : ""}
            </Text>
            <Dialog.Title className="gw-break" mb="0">
              {title}
            </Dialog.Title>
          </Flex>
          <Dialog.Close>
            <IconButton
              aria-label="Close record"
              color="gray"
              size="2"
              type="button"
              variant="ghost"
            >
              <Cross2Icon />
            </IconButton>
          </Dialog.Close>
        </Flex>

        <Box mt="4">
          {loadError ? (
            <Callout.Root color="red" role="alert" size="1">
              <Callout.Text>{loadError}</Callout.Text>
              <Box>
                <Button
                  color="gray"
                  onClick={() => setRetryKey((key) => key + 1)}
                  size="1"
                  type="button"
                  variant="soft"
                >
                  Try again
                </Button>
              </Box>
            </Callout.Root>
          ) : !detail ? (
            <>
              <VisuallyHidden>
                <p role="status">{isLoading ? "Loading record..." : ""}</p>
              </VisuallyHidden>
              <DetailSkeleton />
            </>
          ) : isEditing ? (
            <EditForm
              brands={brands}
              detail={detail}
              errors={errors}
              isSubmitting={isSubmitting}
              onCancel={() => {
                setIsEditing(false);
                setErrors({});
                setSaveError("");
              }}
              onChange={changeValue}
              onSubmit={save}
              saveError={saveError}
              values={values}
            />
          ) : (
            <Flex direction="column" gap="5">
              {lockedReason && (
                <Callout.Root color="gray" size="1" variant="surface">
                  <Callout.Icon>
                    <LockClosedIcon />
                  </Callout.Icon>
                  <Callout.Text>{lockedReason}</Callout.Text>
                </Callout.Root>
              )}
              <RecordFacts detail={detail} />
              <Separator size="4" />
              <Citations detail={detail} />
              {(canEdit || actions) && (
                <Flex gap="3" justify="end" wrap="wrap">
                  {canEdit && (
                    <Button onClick={startEditing} type="button" variant="soft">
                      <Pencil1Icon />
                      Edit
                    </Button>
                  )}
                  {actions}
                </Flex>
              )}
            </Flex>
          )}
        </Box>
      </Dialog.Content>
    </Dialog.Root>
  );
}
